import React, { FC } from "react";
import classnames from "classnames";

import AvatarImage from "./AvatarImage";
import "./index.scss";

interface InitialsAvatarProps {
  firstName: string;
  lastName: string;
  size: 26 | 40 | 44 | 52;
  /** google-profile-pictures-plan.md Phase 2 step 2: the person's photo URL, if any. Renders the
   *  photo in place of the initials, falling back to them for no URL or a failed load (AvatarImage's
   *  own JSDoc). */
  imageUrl?: string | null;
}

// SPARK/Avatar (Figma, cited in overview-leaderboard-card-plan.md): "ONE axis: size -- because the
// initials type size changes with it." Keyed on the SAME 26|40|52 union `size` is typed with, so a
// size this map has no entry for cannot compile -- the box dimensions themselves live in SCSS
// (`.initials-avatar--26/40/52`), verified in the browser; only the type size needs to be dynamic AND
// testable under jest (SCSS is stubbed there), so it alone is set inline.
const INITIALS_FONT_SIZE: Record<26 | 40 | 44 | 52, number> = {
  26: 10,
  40: 13,
  // 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §4: the drawer header's own 44px avatar, "initials white 15px
  // Black" per the frame -- a third size, not a new component, since the 26->10/40->13 lookup already
  // established is exactly what makes a mismatched size/font pair unwritable.
  44: 15,
  52: 16,
};

/**
 * Derives two-letter initials from a person's first/last name, uppercased. No existing initials
 * helper exists anywhere in web (overview-leaderboard-card-plan.md) -- this is the first.
 *
 * @param firstName - the person's first name.
 * @param lastName - the person's last name.
 * @returns the first letter of each name, uppercased and concatenated (e.g. "Ada"/"Lovelace" -> "AL").
 */
export const getInitials = (firstName: string, lastName: string): string =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

/**
 * SPARK/Avatar (Figma): a circular initials badge with exactly ONE axis of variation, `size` -- never
 * a second prop for the initials' own type size, because baking the 26->10/40->13/52->16 lookup in
 * here is what makes a mismatched pair unwritable. No avatar images exist on the leaderboard rows this
 * renders for (out of scope per the plan), so this is the only avatar treatment this card has.
 *
 * @param props.firstName - the person's first name, used to derive the initials shown.
 * @param props.lastName - the person's last name, used to derive the initials shown.
 * @param props.size - the avatar's diameter in px; also selects the initials' own type size.
 * @param props.imageUrl - the person's photo URL, if any (see this prop's own interface comment).
 */
const InitialsAvatar: FC<InitialsAvatarProps> = (props) => {
  const { firstName, lastName, size, imageUrl } = props;

  return (
    <div className={classnames("initials-avatar", `initials-avatar--${size}`)} data-testid="initials-avatar">
      <AvatarImage
        imageUrl={imageUrl}
        fallback={<span style={{ fontSize: INITIALS_FONT_SIZE[size] }}>{getInitials(firstName, lastName)}</span>}
      />
    </div>
  );
};

export default InitialsAvatar;
